// =============================================================================
// Role Access — per-role editor of ActionMaster entitlements.
// IT only (admin-equivalent role on D99); the API enforces the same
// rule server-side (RoleAccessController).
//
// Landing shows a dropdown of ALL Epicor role codes (GET /api/RoleCd).
// Picking a role loads its saved RoleActions row into the checkbox list; a
// role with no row yet shows every action unchecked, and Save creates the
// row (PUT /api/RoleAccess/{roleCd} upserts). Users pick up changes at
// their NEXT login (the JWT carries the actions).
//
// Layout: the shell (.dyn-search-shell) is a fixed-height flex column, so the
// header + toolbar stay pinned and the action list scrolls INSIDE the card —
// the page itself never scrolls (this keeps the header clear of the navbar).
// =============================================================================
import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FaUserShield } from "react-icons/fa";
import { apiFetch } from "./utils/auth";
import "./pagecss/QuoteWizard.css";
import "./pagecss/DynamicQw.css";

const API_URL = process.env.REACT_APP_API_URL;

const csvToSet = (csv) => new Set((csv || "").split(",").filter(Boolean));
const sameSet = (a, b) => {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
};

export default function RoleAccess() {
  const [actions, setActions] = useState(null);      // [{actionId, description}]
  const [roleCds, setRoleCds] = useState([]);        // Epicor roles [{roleCode, roleDescription}]
  const [savedByRole, setSavedByRole] = useState({}); // roleCd -> Set (existing RoleActions rows)
  const [selected, setSelected] = useState("");      // selected roleCode
  const [edited, setEdited] = useState(new Set());   // checkbox state for the selected role
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);          // {kind:'ok'|'err', msg}

  const load = async () => {
    setError(null);
    try {
      const [rolesResp, matrixResp] = await Promise.all([
        apiFetch(`${API_URL}/api/RoleCd`),
        apiFetch(`${API_URL}/api/RoleAccess/matrix`),
      ]);
      const rolesJson = await rolesResp.json().catch(() => null);
      const matrixJson = await matrixResp.json().catch(() => null);
      if (!rolesResp.ok) throw new Error(rolesJson?.error || `HTTP ${rolesResp.status} loading role codes`);
      if (!matrixResp.ok) throw new Error(matrixJson?.error || `HTTP ${matrixResp.status} loading entitlements`);

      // RoleCdDTO serializes its members PascalCase ([JsonPropertyName]
      // attributes) while the wrapper is camelCase — normalize both shapes.
      const codes = (rolesJson?.roleCds || rolesJson?.RoleCds || [])
        .map((r) => ({
          roleCode: r.roleCode ?? r.RoleCode ?? "",
          roleDescription: r.roleDescription ?? r.RoleDescription ?? "",
          inactive: r.inactive ?? r.Inactive ?? false,
        }))
        .filter((r) => r.roleCode && !r.inactive)
        .sort((a, b) => a.roleCode.localeCompare(b.roleCode));

      const savedMap = {};
      (matrixJson?.roles || []).forEach((r) => { savedMap[r.roleCd] = csvToSet(r.actionIds); });

      setRoleCds(codes);
      setActions(matrixJson?.actions || []);
      setSavedByRole(savedMap);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Group actions by the "<Module> - <action>" description convention.
  const groups = useMemo(() => {
    const byModule = new Map();
    (actions || []).forEach((a) => {
      const idx = (a.description || "").indexOf(" - ");
      const moduleName = idx > 0 ? a.description.slice(0, idx) : "Other";
      const label = idx > 0 ? a.description.slice(idx + 3) : a.description;
      if (!byModule.has(moduleName)) byModule.set(moduleName, []);
      byModule.get(moduleName).push({ ...a, label });
    });
    return [...byModule.entries()];
  }, [actions]);

  const savedSet = savedByRole[selected] || null;    // null = no row yet
  const isNewRole = !!selected && savedSet === null;
  const dirty = !!selected && !sameSet(edited, savedSet || new Set());

  const pickRole = (roleCode) => {
    setSelected(roleCode);
    setEdited(new Set(savedByRole[roleCode] || []));
    setFlash(null);
  };

  const toggle = (actionId) => {
    setEdited((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const discard = () => {
    setEdited(new Set(savedByRole[selected] || []));
    setFlash(null);
  };

  const save = async () => {
    setSaving(true);
    setFlash(null);
    try {
      const r = await apiFetch(`${API_URL}/api/RoleAccess/${encodeURIComponent(selected)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actionIds: [...edited] }),
      });
      const json = await r.json().catch(() => null);
      if (!r.ok) throw new Error(json?.error || `HTTP ${r.status}`);
      setSavedByRole((prev) => ({ ...prev, [selected]: new Set(edited) }));
      setFlash({
        kind: "ok",
        msg: json?.created
          ? `Entitlements created for ${selected}. Changes apply at each user's next login.`
          : "Saved. Changes apply at each user's next login.",
      });
    } catch (e) {
      setFlash({ kind: "err", msg: `Save failed — ${e.message}` });
    }
    setSaving(false);
  };

  return (
    <div style={{ display: "flow-root" }}>
      <div className="qw-root content-halo dyn-search-shell">
        {/* Pinned header */}
        <div className="qw-header qw-header-centered" style={{ marginBottom: 10, flexShrink: 0 }}>
          <div className="qw-header-icon"><FaUserShield style={{ fontSize: 20 }} /></div>
          <div className="qw-header-title">Role Access</div>
        </div>

        {error ? (
          <div className="qw-card qw-pad">
            <div className="qw-info qw-info-amber" style={{ background: "#FEF2F2", color: "#991B1B" }}>{error}</div>
          </div>
        ) : !actions ? (
          <div className="qw-card qw-pad" style={{ textAlign: "center", color: "#64748B" }}>
            <Spinner animation="border" size="sm" /> Loading roles…
          </div>
        ) : (
          <div className="qw-card" style={{
            display: "flex", flexDirection: "column", flex: 1, minHeight: 0,
            padding: "14px 16px 12px",
          }}>
            {/* Pinned toolbar: role picker + status + save/discard */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              gap: 10, flexWrap: "wrap", marginBottom: 10, flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label htmlFor="role-access-role" style={{ fontSize: 13, fontWeight: 600, color: "#334155", margin: 0 }}>
                  Role
                </label>
                <select
                  id="role-access-role"
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: 260, fontSize: 13 }}
                  value={selected}
                  disabled={saving}
                  onChange={(e) => pickRole(e.target.value)}
                >
                  <option value="">-- Select a role --</option>
                  {roleCds.map((r) => (
                    <option key={r.roleCode} value={r.roleCode}>
                      {r.roleCode}{r.roleDescription ? ` — ${r.roleDescription}` : ""}
                    </option>
                  ))}
                </select>
                {selected && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
                    background: isNewRole ? "#FEF3C7" : "#F0FDF4",
                    color: isNewRole ? "#B45309" : "#15803D",
                  }}>
                    {isNewRole ? "No entitlements saved yet" : `${(savedSet || new Set()).size} actions saved`}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {flash && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: flash.kind === "ok" ? "#16A34A" : "#B91C1C" }}>
                    {flash.msg}
                  </span>
                )}
                {dirty && (
                  <>
                    <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={saving} onClick={discard}>Discard</button>
                    <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={saving} onClick={save}>
                      {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : "Save changes"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {!selected ? (
              <div style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                color: "#64748B", fontSize: 14, border: "1px dashed #E2E8F0", borderRadius: 8,
              }}>
                Select a role above to view and edit what it may do.
              </div>
            ) : (
              <>
                <p className="qw-sub" style={{ fontSize: 12, margin: "0 0 8px", flexShrink: 0 }}>
                  Tick / untick what <strong>{selected}</strong> may do.
                  {isNewRole
                    ? " This role has no saved entitlements — saving will create them."
                    : " Changes apply at each user's next login."}
                </p>

                {/* Action list scrolls INSIDE this box. */}
                <div style={{ flex: 1, minHeight: 0, overflow: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                  <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, textAlign: "left", minWidth: 250 }}>Action</th>
                        <th style={{ ...thStyle, width: 110 }}>Allowed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map(([moduleName, moduleActions]) => (
                        <React.Fragment key={moduleName}>
                          <tr>
                            <td colSpan={2} style={{
                              padding: "10px 10px 6px", fontWeight: 700, fontSize: 12, letterSpacing: "0.4px",
                              color: "#0052A5", textTransform: "uppercase", borderBottom: "2px solid #E2E8F0",
                              background: "#FBFCFE",
                            }}>
                              {moduleName}
                            </td>
                          </tr>
                          {moduleActions.map((a) => {
                            const on = edited.has(a.actionId);
                            const changed = on !== (savedSet || new Set()).has(a.actionId);
                            return (
                              <tr key={a.actionId}>
                                <td style={{ ...tdStyle, textAlign: "left" }}>
                                  <span className="qw-mono" style={{ color: "#94A3B8", marginRight: 8, fontSize: 11 }}>{a.actionId}</span>
                                  {a.label}
                                </td>
                                <td style={{ ...tdStyle, background: changed ? "#FEF3C7" : on ? "#F0FDF4" : undefined }}>
                                  <input
                                    type="checkbox"
                                    checked={on}
                                    disabled={saving}
                                    onChange={() => toggle(a.actionId)}
                                    aria-label={`${selected} — ${a.label}`}
                                    style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#0052A5" }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Sticky header row: stays visible while the list scrolls inside its box.
const thStyle = {
  padding: "8px", borderBottom: "2px solid #CBD5E1", color: "#334155",
  fontSize: 12, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap",
  background: "#F8FAFC", position: "sticky", top: 0, zIndex: 2,
};

const tdStyle = {
  padding: "5px 8px", borderBottom: "1px solid #F1F5F9", textAlign: "center",
  whiteSpace: "nowrap",
};
